import { usePlayground } from "@/components/Playground2/playground-store";
import { SparklesIcon } from "@heroicons/react/outline";
import { SparklesIcon as SparklesIcon2 } from "@heroicons/react/solid";
import { Button } from "../ui/button";
import { useCallback, useEffect, useState } from "react";
import { Textarea } from "../ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import LoadingSpinner from "../LoadingSpinner";
import { useAIPromptAssisstant } from "@/queries/playground/useAIPromptAssisstant";
import { AnimatePresence } from "framer-motion";
import { motion } from "framer-motion";
import { BasicTooltip } from "../ui/Tooltip";
import { DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Dialog } from "../ui/dialog";
import { RawBlueprintDiffViewer } from "../ui/CommitMessageDialog/BlueprintDiffViewer";

export const AIPromptAssistant = () => {
  const playground = usePlayground();
  const [prompt, setPrompt] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isDiffDialogOpen, setIsDiffDialogOpen] = useState(false);
  const [isShaking, setIsShaking] = useState(false);

  const {
    mutate: generatePrompt,
    isLoading,
    data: generatedPrompt,
  } = useAIPromptAssisstant();

  const handleGeneratePrompt = useCallback(() => {
    if (!prompt.trim()) return;

    setIsOpen(false);
    playground.setIsAIPromptAssistantLoading(true);
    generatePrompt(
      { prompt, blueprint: playground.blueprint },
      {
        onSuccess: () => {
          playground.setIsAIPromptAssistantLoading(false);
          setPrompt("");
        },
        onError: () => {
          playground.setIsAIPromptAssistantLoading(false);
        },
      },
    );
  }, [generatePrompt, playground, prompt]);

  useEffect(() => {
    if (!isOpen && !isLoading) {
      setPrompt("");
    }
  }, [isOpen, isLoading]);

  useEffect(() => {
    if (generatedPrompt) {
      setIsDiffDialogOpen(true);
    }
  }, [generatedPrompt]);

  const handleApplyPrompt = useCallback(() => {
    if (!generatedPrompt) return;

    playground.applyPromptBlueprintToState(generatedPrompt);
    setIsDiffDialogOpen(false);
  }, [generatedPrompt, playground]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (!prompt.trim()) {
          setIsShaking(true);
          return;
        }
        handleGeneratePrompt();
      }
    },
    [handleGeneratePrompt, prompt],
  );

  const handleDiscard = useCallback(() => {
    setIsDiffDialogOpen(false);
    setPrompt("");
    setIsOpen(false);
  }, []);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      if (!open) {
        handleDiscard();
      }
    },
    [handleDiscard],
  );

  return (
    <div className="relative">
      <Popover open={isOpen} onOpenChange={handleOpenChange}>
        <PopoverTrigger>
          <BasicTooltip
            content={isLoading ? "Generating..." : "Generate prompt"}
          >
            <Button variant="ghost" size="icon">
              <AnimatePresence mode="wait" initial={false}>
                <motion.span
                  key={isLoading ? "loading" : "idle"}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.2 }}
                  className="flex"
                >
                  {isLoading ? (
                    <SparklesIcon2 className="duration-750 h-5 w-5 animate-pulse text-blue-500 transition-all" />
                  ) : (
                    <SparklesIcon className="h-5 w-5" />
                  )}
                </motion.span>
              </AnimatePresence>
            </Button>
          </BasicTooltip>
        </PopoverTrigger>
        <PopoverContent className="w-[350px]" align="center">
          <span className="text-sm font-medium">Generate a prompt with AI</span>
          <motion.div
            className="w-full"
            animate={isShaking ? { x: [0, -8, 8, -8, 8, 0] } : { x: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            onAnimationComplete={() => setIsShaking(false)}
          >
            <Textarea
              disabled={isLoading}
              onKeyDown={handleKeyDown}
              placeholder="Create a prompt that writes a poem from a desired language on a specific topic"
              className="mt-2 min-h-[100px]"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
            />
          </motion.div>
          <BasicTooltip shouldShow={!prompt} content="Enter a prompt to begin">
            <span className="text-sm font-medium">
              <Button
                variant="outline"
                disabled={isLoading || !prompt.trim()}
                className="group mt-2 w-full"
                onClick={handleGeneratePrompt}
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    Generating...
                    <LoadingSpinner className="ml-2" size={4} />
                  </div>
                ) : (
                  <span className="flex items-center gap-2">
                    Generate
                    <span className="group-hover:opacity-85 select-none opacity-60 transition-opacity duration-200 ease-in-out">
                      ↩
                    </span>
                  </span>
                )}
              </Button>
            </span>
          </BasicTooltip>
        </PopoverContent>
      </Popover>
      <Dialog open={isDiffDialogOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>PromptLayer AI Assistant</DialogTitle>
          </DialogHeader>
          {generatedPrompt && (
            <RawBlueprintDiffViewer
              oldBlueprint={playground.blueprint}
              newBlueprint={generatedPrompt}
            />
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleDiscard}>
              Discard
            </Button>
            <Button onClick={handleApplyPrompt}>Apply</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
